import type { AdProvider, AdTrendPoint, AdvertisingOverviewTrend } from '@/api/advertisingOverview'
import { Card, DualSeriesChart, EmptyState, formatCurrency, formatInteger, majorUnits } from '@/design'
import { formatMoney } from '@/features/analytics/advertising/metrics'
import { CardQueryBody } from '@/features/overview/CardQueryBody'
import { formatDateKey, type GlobalFilters } from '@/lib/filters'

import { ALL_SALES_CAVEAT, NOT_AVAILABLE_TEXT, UNDEFINED_FIGURE, oneUnit } from './definitions'
import { advertisingSectionError, isAdvertisingFlagOff } from './errors'
import { ALL_SALES_WEEKS, useAllSalesTrend } from './overviewQuery'
import { FlagOffNote } from './SectionStates'

/** A week's sales per one unit of spend, as the KPI tile reads it; "—" without spend or sales. */
function perSpend(point: AdTrendPoint, currency: string): string {
  if (point.revenueGrossMinor === null || point.spendMinor === 0) return UNDEFINED_FIGURE
  return formatCurrency(point.revenueGrossMinor / point.spendMinor, { currency })
}

function weekLabel(point: AdTrendPoint): string {
  return `${formatDateKey(point.date)} – ${formatDateKey(point.endDate, true)}`
}

/** The chart and the weeks behind it, or why there are none. */
function AllSalesBody({ data }: { data: AdvertisingOverviewTrend }) {
  if (data.notAvailable) {
    const text = NOT_AVAILABLE_TEXT[data.notAvailable]
    return <EmptyState variant="not-live" compact title={text.label} description={text.why} />
  }
  const currency = data.meta.currency
  const points = data.points.map((point) => ({
    label: formatDateKey(point.date),
    detail: weekLabel(point),
    primary: majorUnits(point.spendMinor, currency),
    secondary: point.revenueGrossMinor === null ? null : majorUnits(point.revenueGrossMinor, currency),
  }))
  return (
    <>
      {data.salesUnavailable ? (
        <p className="aov-note" role="status" data-testid="all-sales-unavailable">
          A sale in another currency could not be converted for this range, so sales and orders are not shown.
        </p>
      ) : null}
      <DualSeriesChart
        title="Meta spend and all sales by week"
        points={points}
        primaryLabel="Ad spend (Meta)"
        secondaryLabel="All sales (every completed order)"
        format="currency"
        formatOptions={{ currency }}
      />
      <div className="ci-table-wrap" role="region" aria-label="Meta spend, orders and sales by week" tabIndex={0}>
        <table className="ci-table">
          <thead>
            <tr>
              <th scope="col">Week</th>
              <th scope="col">Meta spend</th>
              <th scope="col">Orders</th>
              <th scope="col">All sales</th>
              <th scope="col">Per {oneUnit(currency)} of spend</th>
            </tr>
          </thead>
          <tbody>
            {data.points.map((point) => (
              <tr key={point.date}>
                <th scope="row">{weekLabel(point)}</th>
                <td>{formatMoney(point.spendMinor, currency)}</td>
                <td>{point.orders === null ? UNDEFINED_FIGURE : formatInteger(point.orders)}</td>
                <td>{formatMoney(point.revenueGrossMinor, currency)}</td>
                <td>{perSpend(point, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

/**
 * Meta's spend (columns) against every completed order's gross (a line), by week over the
 * last twelve weeks to the range's end, with each week's orders: whether the business
 * moves with spend, whoever gets the credit. Not attribution, and it says so. Its own
 * query, independent of the attribution model.
 */
export function AllSalesTrendCard({ filters, provider }: { filters: GlobalFilters; provider: AdProvider }) {
  const trend = useAllSalesTrend(filters, provider)
  return (
    <Card
      title={`Meta spend vs all sales — last ${ALL_SALES_WEEKS} weeks`}
      subtitle={`${ALL_SALES_CAVEAT}. Spend reported by Meta; sales are every completed order.`}
      busy={trend.isFetching}
    >
      {isAdvertisingFlagOff(trend.error) ? (
        <FlagOffNote />
      ) : (
        <CardQueryBody
          query={trend}
          loadingRows={5}
          errorDescription={advertisingSectionError(trend.error, 'The spend and sales trend could not be loaded.')}
        >
          {(data) => <AllSalesBody data={data} />}
        </CardQueryBody>
      )}
    </Card>
  )
}
